import React from "react";
import { Box, Paper, Skeleton } from "@mui/material";

// Texto solo para lectores de pantalla.
const ocultoVisual = {
  border: 0,
  clip: "rect(0 0 0 0)",
  height: "1px",
  margin: "-1px",
  overflow: "hidden",
  padding: 0,
  position: "absolute",
  whiteSpace: "nowrap",
  width: "1px",
};

// Respeta "reducir movimiento": el esqueleto queda quieto.
const sinAnimacion = {
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

const Contenedor = ({ etiqueta, children, sx }) => (
  <Box role="status" aria-live="polite" sx={sx}>
    <Box component="span" sx={ocultoVisual}>
      {etiqueta}
    </Box>
    <Box aria-hidden="true">{children}</Box>
  </Box>
);

/**
 * Esqueleto de una tabla mientras carga: una fila de encabezado y `filas` filas de datos.
 *
 * @param {object} props
 * @param {number} [props.filas] Filas de datos (por defecto 5).
 * @param {number} [props.columnas] Columnas (por defecto 4).
 * @param {string} [props.etiqueta] Texto para lectores de pantalla (por defecto "Cargando…").
 */
export const CargandoTabla = ({ filas = 5, columnas = 4, etiqueta = "Cargando…" }) => (
  <Contenedor etiqueta={etiqueta} sx={{ width: "100%" }}>
    <Box sx={{ display: "grid", gap: 1.5 }}>
      {Array.from({ length: filas + 1 }, (_, fila) => (
        <Box
          key={fila}
          sx={{
            display: "grid",
            gridTemplateColumns: `repeat(${columnas}, minmax(0, 1fr))`,
            gap: 2,
            alignItems: "center",
          }}
        >
          {Array.from({ length: columnas }, (_, col) => (
            <Skeleton
              key={col}
              variant="text"
              sx={{
                fontSize: fila === 0 ? "1.25rem" : "1rem",
                ...sinAnimacion,
              }}
            />
          ))}
        </Box>
      ))}
    </Box>
  </Contenedor>
);

/**
 * Esqueleto de tarjetas (p. ej. los KPI del panel) mientras cargan.
 *
 * @param {object} props
 * @param {number} [props.cantidad] Número de tarjetas (por defecto 4).
 * @param {string} [props.etiqueta] Texto para lectores de pantalla (por defecto "Cargando…").
 */
export const CargandoTarjetas = ({ cantidad = 4, etiqueta = "Cargando…" }) => (
  <Contenedor etiqueta={etiqueta} sx={{ width: "100%" }}>
    <Box
      sx={{
        display: "grid",
        gap: 2,
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
      }}
    >
      {Array.from({ length: cantidad }, (_, i) => (
        <Paper key={i} sx={{ p: 2.5 }}>
          <Skeleton variant="text" width="45%" sx={sinAnimacion} />
          <Skeleton
            variant="text"
            width="70%"
            sx={{ fontSize: "2rem", ...sinAnimacion }}
          />
          <Skeleton variant="text" width="35%" sx={sinAnimacion} />
        </Paper>
      ))}
    </Box>
  </Contenedor>
);
